import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import {
  getAssistantContext,
  hasAssistantContext,
} from '@/lib/assistantContext';
import { quickActionsFor } from '@/lib/assistantQuickActions';
import type { QuickAction } from '@/lib/assistantQuickActions';

type Props = {
  /** Which page/tab's chips to render. When no context is registered
   *  under this key (or no chips defined for it), the rack renders
   *  nothing — zero added chrome for tabs that haven't opted in. */
  contextKey: string;
  /**
   * Fires when the clinician clicks a chip. Receives:
   *  - `displayText`: what to show in the user chat bubble
   *  - `promptText`: the full prompt sent to the model (with context)
   * The chat component is responsible for adding the optimistic user
   * bubble using `displayText` and sending `promptText` upstream.
   */
  onChip: (displayText: string, promptText: string) => void;
  /** Disabled while a send is in flight. */
  disabled?: boolean;
};

/**
 * Compact chip rack above the RxAssistant input. Only renders when
 * the current page has registered a context AND at least one chip is
 * defined for that context. Chips are deliberately small — UI
 * cleanliness comes first; we can grow the rack later if usage
 * warrants more prominence.
 */
export const AssistantQuickActions = ({
  contextKey,
  onChip,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const chips = quickActionsFor(contextKey);
  if (chips.length === 0 || !hasAssistantContext(contextKey)) return null;

  const onClick = (action: QuickAction) => {
    const ctx = getAssistantContext(contextKey);
    if (!ctx) return; // context vanished between render and click
    const prompt = action.buildPrompt(ctx);
    onChip(action.label, prompt);
  };

  return (
    <div
      className="mb-1.5 flex flex-wrap items-center gap-1.5 px-2 pt-1.5"
      aria-label={t('Quick actions')}
    >
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          onClick={() => onClick(chip)}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-full border border-input bg-card px-2 py-0.5 text-[0.6875rem] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Sparkles className="h-3 w-3 text-primary" />
          {chip.label}
        </button>
      ))}
    </div>
  );
};
